<template>
  <div class="grouped-log-details">
    <div v-if="!isProjectSummaryHoursValid(this.selected_grouped_log.grouped_log)" class="gld-notice">
      <b-icon icon="alert-circle" size="is-small" type="is-danger"/>
      <span>{{ this.$t('project-summary-view.invalid-summary-notice') }}</span>
    </div>

    <!-- Tasks -->
    <div class="gld-section">
      <div class="gld-section__head">
        <span class="gld-section__icon"><b-icon icon="format-list-checks" size="is-small"/></span>
        <span class="gld-section__title">{{ this.$t('grouped-log-details.tasks-title') }}</span>
      </div>
      <b-table class="gld-table" :data="this.selected_grouped_log.grouped_log.tasks"
               :mobile-cards="false" :loading="isLoading" :scrollable="true" :show-header="true">
        <b-table-column field="task_title" v-slot="props" :label="this.$t('grouped-log-details.task-title-column')">
          {{ props.row.title }}
        </b-table-column>
        <b-table-column field="internal_duration" v-slot="props"
                        :label="this.$t('grouped-log-details.internal-duration-column')">
          <p class="internal-text">{{ getFormattedTimeHour(props.row.logged_durations_internal) }}</p>
        </b-table-column>
        <b-table-column field="external_duration" v-slot="props"
                        :label="this.$t('grouped-log-details.external-duration-column')">
          <p class="external-text">{{ getFormattedTimeHour(props.row.logged_durations_external) }}</p>
        </b-table-column>
        <b-table-column field="logged_quantities" v-slot="props" :label="this.$t('grouped-log-details.quantity-column')">
          <p>{{ getFormattedQuantity({quantity: props.row.logged_quantities}, props.row.unit) }}</p>
        </b-table-column>
        <b-table-column field="expected_quantities" v-slot="props"
                        :label="this.$t('grouped-log-details.expected-quantity-column')">
          <p>{{ getFormattedQuantity({quantity: props.row.expected_quantities}, props.row.unit) }}</p>
        </b-table-column>
        <b-table-column field="expected_performance" v-slot="props"
                        :label="this.$t('grouped-log-details.performance-column')">
          <p>{{ getFormattedPerformance(props.row.expected_performance, props.row.unit) }}</p>
        </b-table-column>
        <b-table-column field="profit" v-slot="props" :label="this.$t('grouped-log-details.profit-column')">
          <p :style="profitTextStyle(props.row.profit)">{{ getFormattedPricing(props.row.profit) }}</p>
        </b-table-column>
      </b-table>
    </div>

    <!-- Personnel -->
    <div class="gld-section">
      <div class="gld-section__head">
        <span class="gld-section__icon"><b-icon icon="account-hard-hat" size="is-small"/></span>
        <span class="gld-section__title">{{ this.$t('grouped-log-details.personnel-title') }}</span>
      </div>
      <b-table class="gld-table" :data="this.selected_grouped_log.grouped_log.personnel"
               :mobile-cards="false" :loading="isLoading" :scrollable="true" :show-header="true">
        <b-table-column field="personal_name" v-slot="props"
                        :label="this.$t('grouped-log-details.personal-name-column')">
          {{ getFullName(props.row.name) }}
        </b-table-column>
        <b-table-column field="logged_quantities" v-slot="props"
                        :label="this.$t('grouped-log-details.personal-quantity-column')">
          <p :class="personalQuantityTextStyle(props.row.group)">{{ getFormattedTimeHour(props.row.logged_quantities) }}</p>
        </b-table-column>
        <b-table-column field="personal_agency" v-slot="props"
                        :label="this.$t('grouped-log-details.personal-agency-column')">
          {{ props.row.agency }}
        </b-table-column>
      </b-table>
    </div>

    <!-- Totals: tasks hours vs personnel hours -->
    <div class="gld-totals">
      <div class="gld-totals__row">
        <span class="gld-totals__label">{{ this.$t('grouped-log-details.tasks-total-hours') }}</span>
        <span class="gld-totals__val internal-text">{{
            getFormattedTimeHour(this.selected_grouped_log.grouped_log.total_logged_durations_internal)
          }}</span>
        <span class="gld-totals__val external-text">{{
            getFormattedTimeHour(this.selected_grouped_log.grouped_log.total_logged_durations_external)
          }}</span>
      </div>
      <div class="gld-totals__row">
        <span class="gld-totals__label">{{ this.$t('grouped-log-details.personnel-total-hours') }}</span>
        <span class="gld-totals__val internal-text">{{
            getFormattedTimeHour(this.selected_grouped_log.grouped_log.total_logged_durations_internal_personnel)
          }}</span>
        <span class="gld-totals__val external-text">{{
            getFormattedTimeHour(this.selected_grouped_log.grouped_log.total_logged_durations_external_personnel)
          }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GroupedLogDetails",
  components: {},
  props: {
    'selected_grouped_log': {
      grouped_log: {}
    }
  },
  data() {
    return {
      isLoading: false,
    }
  },
  mounted() {
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.grouped-log-details {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.gld-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: $omx-radius-md;
  background: $omx-danger-100;
  color: $omx-danger-600;
  font-weight: 600;
  font-size: 13px;
}

.gld-section__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.gld-section__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: $omx-brand-50;
  color: $omx-brand-600;
}
.gld-section__title {
  font-size: 15px;
  font-weight: 600;
  color: $omx-ink-900;
}

// Tokenised tables: small uppercase headers, hairline row separators, no heavy chrome.
::v-deep .gld-table table { background: transparent; }
::v-deep .gld-table th {
  border: 0 !important;
  border-bottom: 1px solid $omx-ink-100 !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: $omx-ink-500;
  padding: 8px 10px;
}
::v-deep .gld-table td {
  border: 0;
  border-bottom: 1px solid $omx-ink-100;
  padding: 9px 10px;
  vertical-align: middle;
  font-variant-numeric: tabular-nums;
}
::v-deep .gld-table tbody tr:last-child td { border-bottom: 0; }
::v-deep .gld-table tbody tr:hover td { background: $omx-ink-50; }
::v-deep .gld-table p { margin: 0; }

.gld-totals {
  border: 1px solid $omx-ink-100;
  border-radius: $omx-radius-md;
  background: $omx-ink-50;
  padding: 6px 14px;
}
.gld-totals__row {
  display: grid;
  grid-template-columns: 1fr 90px 90px;
  align-items: center;
  gap: 8px;
  padding: 8px 0;

  & + & { border-top: 1px solid $omx-ink-100; }
}
.gld-totals__label { font-size: 13px; color: $omx-ink-700; }
.gld-totals__val { text-align: right; font-variant-numeric: tabular-nums; }
</style>
